// import {accountAtom} from '@/atom/account/account';
// import {blackTransparentLoaderVisibleAtom} from '@/atom/loader';
// import {centerToastAtom, toastV2Atom} from '@/atom/toast';
// import {GetTripQueryCGType} from '@/types/graphql/operations';
import { TRIP_CONDITIONS, TripModeType } from '@/app/ui/trip/client-trip-page';
import { blackTransparentLoaderModalAtom, centerToastModalAtom } from '@/atom/modal';
import { globalEditUserAtom, globalIamEditAtom } from '@/atom/trip';
import { SigninResultCGType, SurveyCGType } from '@/types/graphql/root';
import { Client } from '@stomp/stompjs';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

const useTripSocket = ({
  mode,
  tripData,
  refetchTrip,
  signinResult,
}: {
  mode: TripModeType;
  tripData: SurveyCGType | null;
  refetchTrip: () => Promise<void>;
  signinResult: SigninResultCGType | null;
}) => {
  const [stompClient, setStompClient] = useState<Client | null>(null);
  const [editUser, setEditUser] = useState<string | null>(null);
  const [iamEdit, setIamEdit] = useState<boolean>(false);
  const setGlobalEditUser = useSetAtom(globalEditUserAtom);
  const setGlobalIamEdit = useSetAtom(globalIamEditAtom);
  // const account = useAtomValue(accountAtom);
  const setCenterToast = useSetAtom(centerToastModalAtom);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const setBlackTransparentLoaderModal = useSetAtom(blackTransparentLoaderModalAtom);

  const onMessage = useCallback(
    (body: string) => {
      if (body === 'update') {
        setLastUpdatedAt(Date.now());
        return;
      }

      try {
        const payload = JSON.parse(body);
        // console.log(payload);
        if (payload.isEdit) {
          setEditUser(payload.userName);
          setGlobalEditUser(payload.userName);
        } else {
          setEditUser(null);
          setGlobalEditUser(null);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [iamEdit],
  );

  useEffect(() => {
    // if (!lastUpdatedAt || iamEdit) return;
    if (!lastUpdatedAt) return;

    setBlackTransparentLoaderModal(true);
    refetchTrip().finally(() => {
      setBlackTransparentLoaderModal(false);
    });
  }, [lastUpdatedAt]);

  useEffect(() => {
    const client = new Client({
      brokerURL: process.env.NEXT_PUBLIC_STOMP_ENDPOINT,
      // debug: (str) => {
      //   console.log('=======debug=======\n', str);
      // },
    });
    setStompClient(client);
    client.onConnect = (frame) => {
      client.subscribe(`/topic/survey/${tripData?.id}`, (msg) => {
        onMessage(msg.body);
      });
    };

    client.activate();
    return () => {
      client.deactivate();
    };
  }, [tripData?.id]);

  const subscribeEdit = useCallback(() => {
    if (editUser && !iamEdit) {
      setCenterToast({
        text: `지금 ${editUser}님이 편집 중이에요!\n잠시만 기다려주세요.`,
        renderingTime: 2000,
        iconHidden: true,
      });
      return false;
    }

    if (stompClient?.connected) {
      stompClient.subscribe(`/topic/survey/${tripData?.id}/edit`, (msg) => {}, {
        userName: signinResult?.user?.name || '이름 없는 유저',
      });
      setIamEdit(true);
      setGlobalIamEdit(true);
      return true;
    }

    throw new Error(
      JSON.stringify({
        code: 'UNAUTHORIZED',
        message: '로그인이 만료되었습니다.',
        category: 'AUTH',
      }),
    );
  }, [editUser, iamEdit, stompClient, setCenterToast, tripData?.id, signinResult?.user?.name, setGlobalIamEdit]);

  const unsubscribeEdit = useCallback(() => {
    if (stompClient?.connected) {
      setIamEdit(false);
      setGlobalIamEdit(false);
      stompClient.unsubscribe(`/topic/survey/${tripData?.id}/edit`);
    }
  }, [stompClient, tripData]);

  useEffect(() => {
    if (TRIP_CONDITIONS.IS_VIEW_MODE(mode)) unsubscribeEdit();
  }, [mode, unsubscribeEdit]);

  return {
    editUser,
    subscribeEdit,
    unsubscribeEdit,
    iamEdit,
  };
};

export default useTripSocket;
